import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { WizardStep } from '../types'
import { WIZARD_STEPS } from './wizardSteps'

type Props = { current: WizardStep; onStepClick?: (step: WizardStep) => void }

export function WizardStepper({ current, onStepClick }: Props) {
  const { t } = useTranslation()
  const active = WIZARD_STEPS.find((s) => s.step === current)
  const total = WIZARD_STEPS.length

  return (
    <nav aria-label={t('hr.belgeler.wizard.label')} className="rounded-2xl border bg-card px-3 py-4 shadow-xs sm:px-4 sm:py-5">
      <ol className="grid grid-cols-4">
        {WIZARD_STEPS.map((s, i) => {
          const done = current > s.step
          const isActive = current === s.step
          const clickable = done && !!onStepClick
          return (
            <li key={s.step} className="relative flex justify-center">
              {i < total - 1 ? (
                <span aria-hidden className="absolute left-[calc(50%+28px)] right-[calc(-50%+28px)] top-[17px] h-0.5 overflow-hidden rounded-full bg-border">
                  <span className={cn('block h-full bg-primary transition-[width] duration-300', done ? 'w-full' : 'w-0')} />
                </span>
              ) : null}
              <button
                type="button"
                disabled={!clickable}
                aria-current={isActive ? 'step' : undefined}
                aria-label={t(s.titleKey)}
                onClick={() => onStepClick?.(s.step)}
                className="group relative flex min-w-0 flex-col items-center gap-2 rounded-xl px-1 text-center outline-none disabled:cursor-default focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span
                  className={cn(
                    'flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold tabular-nums transition-all',
                    done && 'bg-primary text-primary-foreground shadow-[0_4px_10px_-3px_color-mix(in_oklab,var(--brand-blue)_50%,transparent)] group-enabled:group-hover:bg-primary/85',
                    isActive && 'border-2 border-primary bg-card text-primary ring-4 ring-primary/12',
                    !done && !isActive && 'border-2 border-border bg-card text-muted-foreground',
                  )}
                >
                  {done ? <Check className="size-4" strokeWidth={3} /> : s.step}
                </span>
                <span className="hidden min-w-0 sm:block">
                  <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('hr.belgeler.wizard.stepN', { n: s.step })}
                  </span>
                  <span
                    className={cn(
                      'block text-[13px] font-semibold leading-snug',
                      isActive ? 'text-primary' : done ? 'text-foreground group-enabled:group-hover:text-primary' : 'text-muted-foreground',
                    )}
                  >
                    {t(s.titleKey)}
                  </span>
                  <span className="mt-0.5 hidden text-[11px] text-muted-foreground xl:block">{t(s.hintKey)}</span>
                </span>
              </button>
            </li>
          )
        })}
      </ol>

      {active ? (
        <div className="mt-4 flex items-center justify-between gap-3 border-t pt-3 sm:hidden">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-primary">{t(active.titleKey)}</p>
            <p className="text-xs text-muted-foreground">{t(active.hintKey)}</p>
          </div>
          <span className="shrink-0 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold tabular-nums text-primary">
            {t('hr.belgeler.wizard.stepOf', { current, total })}
          </span>
        </div>
      ) : null}
    </nav>
  )
}
